import { useState } from 'react'
import style from './style.module.scss'
import { Button } from '@crossfit/design-system'
import Coach from './Coach'
import { Text } from '@crossfit/design-system'
import classnames from 'classnames'
import { useDispatch, useSelector } from 'react-redux'
import {
  patchAffiliateMember,
  patchAffiliateMembersOrdering,
} from 'services/affiliates'
import { useFormNotification } from 'hooks/useFormNotification'
import { ToggleValue } from '../Shared/Toggle'
import { MemberReorder } from 'types'
import { getAffiliate } from 'store/selectors'
import {
  updateAffiliateMemberVisibility,
  updateAffiliateMembers,
} from 'store/slices/affiliate'

export const ItemTypes = {
  COACH: 'coach',
}

export default function Coaches() {
  const [isReordering, setIsReordering] = useState<boolean>(false)
  const dispatch = useDispatch()
  const [dragItemIndex, setDragItemIndex] = useState<number>(null)
  const [dragOverItemIndex, setDragOverItemIndex] = useState<number>(null)
  const handleFormNotification = useFormNotification()
  const affiliateDetail = useSelector(getAffiliate)
  const members = affiliateDetail.members

  const handleSort = (e) => {
    e.preventDefault()
    let coachItems = [...members]
    const draggedItemContent = coachItems.splice(dragItemIndex, 1)[0]
    coachItems.splice(dragOverItemIndex, 0, draggedItemContent)

    coachItems = coachItems.map((item, index) => {
      return { ...item, order: index }
    })

    setDragItemIndex(null)
    setDragOverItemIndex(null)
    dispatch(updateAffiliateMembers(coachItems))
  }

  const onCancelReorder = () => {
    setIsReordering(false)
  }

  const onSaveReorder = async () => {
    const membersOrdered: MemberReorder[] = members?.reduce(
      (accumulator, currentValue, currentIndex) => {
        accumulator.push({
          id: currentValue.id,
          order: currentIndex,
        })
        return accumulator
      },
      [],
    )

    const response = await patchAffiliateMembersOrdering(
      affiliateDetail?.id,
      membersOrdered,
    )

    if (response.status < 300) {
      const newMembers = members?.map((member) => ({
        ...member,
        order: membersOrdered.find((coach) => coach.id === member.id).order,
      }))

      dispatch(updateAffiliateMembers(newMembers))
      handleFormNotification('success')
      setIsReordering(false)
    } else {
      handleFormNotification('error', { message: 'Something went wrong' })
    }
  }

  const toggleCoach = async (newtoggleValue: ToggleValue, userId: number) => {
    const response = await patchAffiliateMember(affiliateDetail?.id, userId, {
      isPublic: newtoggleValue === ToggleValue.PUBLIC ? true : false,
    })

    if (response.status < 300) {
      const newMembers = [...members]
      const updatedCoachIndex = newMembers.findIndex((m) => m.id === userId)
      newMembers[updatedCoachIndex] = {
        ...newMembers[updatedCoachIndex],
        isPublic: newtoggleValue === ToggleValue.PUBLIC ? true : false,
      }

      dispatch(
        updateAffiliateMemberVisibility({ userId, visibility: newtoggleValue }),
      )
      handleFormNotification('success')
    } else {
      handleFormNotification('error', { message: 'Something went wrong' })
    }
  }

  const getCoachPrivacySettings = (coachUserId: number) => {
    const member = members?.find((m) => m.id === coachUserId)

    return member.isPublic ? ToggleValue.PUBLIC : ToggleValue.PRIVATE
  }

  if (!members) {
    return null
  }

  return (
    <div className={style.wrapper}>
      <div className={style.headerContainer}>
        <Text type={'body'} size={'sm'}>
          Profiles
        </Text>
        {members.length > 1 ? (
          <div className={style.headerButtonContainer}>
            <Button
              variant='secondary'
              size='md'
              color='white'
              className={style.orderButton}
              onClick={() =>
                isReordering ? onCancelReorder() : setIsReordering(true)
              }
            >
              Change Order
            </Button>
            <Text type={'body'} size={'sm'} className={style.descriptionText}>
              Profiles will appear publicly in the order shown below
            </Text>
          </div>
        ) : null}
      </div>
      <div
        className={classnames(
          style.coachesContainer,
          isReordering && style.dragging,
        )}
      >
        {isReordering
          ? [...members]
              ?.sort((a, b) => a.order - b.order)
              ?.map((m, i) => (
                <div
                  key={i}
                  className={classnames(
                    style.coachWrapper,
                    style.dragging,
                    dragItemIndex === i && style.dragItem,
                    dragOverItemIndex === i && style.dragOverItem,
                  )}
                  draggable
                  onDragStart={() => setDragItemIndex(i)}
                  onDragEnter={() => setDragOverItemIndex(i)}
                  onDragEnd={(e) => handleSort(e)}
                  onDragOver={(e) => e.preventDefault}
                >
                  <Coach
                    key={m.id}
                    name={`${m.firstName} ${m.lastName}`}
                    role={m.roles[0]}
                    certifications={m.certifications}
                    userId={m.id}
                    toggleValue={getCoachPrivacySettings(m.id)}
                    onToggle={(newtoggleValue) => {
                      toggleCoach(newtoggleValue, m.id)
                    }}
                  />
                </div>
              ))
          : [...members]
              ?.sort((a, b) => a.order - b.order)
              .map((m) => (
                <div key={m.id} className={style.coachWrapper}>
                  <Coach
                    name={`${m.firstName} ${m.lastName}`}
                    role={m.roles[0]}
                    certifications={m.certifications}
                    userId={m.id}
                    toggleValue={getCoachPrivacySettings(m.id)}
                    onToggle={(newtoggleValue) => {
                      toggleCoach(newtoggleValue, m.id)
                    }}
                  />
                </div>
              ))}
      </div>
      <div
        className={classnames(style.formControls, isReordering && style.show)}
      >
        <Button
          variant='secondary'
          color='white'
          size='sm'
          onClick={onCancelReorder}
        >
          Cancel
        </Button>
        <Button variant='primary' size='sm' onClick={onSaveReorder}>
          Save
        </Button>
      </div>
    </div>
  )
}
